'use client'
import { memo, useEffect, useMemo, useState, type FC } from 'react'
import { usePathname } from 'next/navigation'
import styled from 'styled-components'
import { motion, type Variants } from 'motion/react'
import { VariantCard } from '../product/VariantCard'
import { useFilters, useProduct, useTranslator } from '@/hooks'
import { E_DELIVERY_TYPE, E_FILTER_TYPE, type TypeCmsVariant } from '@/schema'
import { defaultGrid, getTransition, mq, presets, vw } from '@/styles'
import { LANG } from '@/utils'
import type { TypeActiveFilter, TypeFilter } from '@/context'

type Props = {
  data: TypeCmsVariant[]
  gaList: {
    name: string
    id: string
  }
}

const FROZEN_FILTER_TRANSLATION = {
  de: { name: 'Gefroren', slug: 'gefroren' },
  en: { name: 'Frozen', slug: 'frozen' },
  es: { name: 'Congelados', slug: 'congelados' },
  it: { name: 'Congelati', slug: 'congelati' },
  fr: { name: 'Surgelés', slug: 'surgeles' },
  pt: { name: 'Congelados', slug: 'congelados' },
}

const getAvailableBooleanFilters = (data: TypeCmsVariant[]) => {
  // Collect boolean filters from product filters
  const booleanFilters = data.reduce((acc, item) => {
    item.product?.filters?.forEach(filter => {
      if (filter.type === E_FILTER_TYPE.BOOLEAN) {
        acc[filter.param] = {
          id: filter.id!,
          type: filter.type,
          name: filter.name,
          slug: filter.slug[LANG]
        }
      }
    })
    return acc
  }, {} as Record<string, TypeFilter>)

  // Remove filters that ALL products have (no filtering value)
  const filteredBooleanFilters = Object.entries(booleanFilters).reduce((acc, [key, filter]) => {
    const allProductsHaveFilter = data.every(item =>
      item.product?.filters?.some(
        pf => pf.slug?.[LANG] === filter.slug && pf.type === E_FILTER_TYPE.BOOLEAN
      )
    )

    // Only include if NOT all products have it
    if (!allProductsHaveFilter) {
      acc[key] = filter
    }

    return acc
  }, {} as Record<string, TypeFilter>)

  // Check if there are any frozen products
  const hasFrozenProducts = data.some(item => item.deliveryType === E_DELIVERY_TYPE.FROZEN)

  // If ALL products are frozen, do not add the frozen filter
  const allProductsFrozen = data.every(item => item.deliveryType === E_DELIVERY_TYPE.FROZEN)

  // Add "frozen" filter only if some (but not all) products are frozen
  if (hasFrozenProducts && !allProductsFrozen) {
    filteredBooleanFilters['frozen'] = {
      id: 999999,
      type: E_FILTER_TYPE.BOOLEAN,
      name: FROZEN_FILTER_TRANSLATION[LANG].name,
      slug: FROZEN_FILTER_TRANSLATION[LANG].slug
    }
  }

  return filteredBooleanFilters
}

const getAvailableSelectFilters = (data: TypeCmsVariant[]) => {
  const selectFilters = data.reduce((acc, item) => {
    item.product?.filters?.forEach(filter => {
      if (filter.type === E_FILTER_TYPE.SELECT && filter.values) {
        // Extract each value as a separate TypeFilter (like brands)
        filter.values.forEach(value => {
          if (value.visible !== false) {
            const uniqueKey = `${filter.param}-${value.id}`
            acc[uniqueKey] = {
              id: filter.id!,
              name: value.name || value.value,
              slug: value.value,
              type: filter.type,
              parentParam: filter.param,
              parentName: filter.name,
              parentSlug: filter.slug[LANG]
            }
          }
        })
      }
    })
    return acc
  }, {} as Record<string, TypeFilter & { parentParam: string; parentName: string; parentSlug: string }>)

  // Remove filter values that ALL products have (no filtering value)
  const filteredSelectFilters = Object.entries(selectFilters).reduce((acc, [key, filter]) => {
    const allProductsHaveValue = data.every(item =>
      item.product?.filters?.some(pf =>
        pf.slug?.[LANG] === filter.parentSlug &&
        pf.type === E_FILTER_TYPE.SELECT &&
        pf.values?.some(v => v.value === filter.slug)
      )
    )

    // Only include if NOT all products have this value
    if (!allProductsHaveValue) {
      acc[key] = filter
    }

    return acc
  }, {} as Record<string, TypeFilter & { parentParam: string; parentName: string; parentSlug: string }>)

  // Group by parent param (like grouping all "format" values together)
  const grouped = Object.values(filteredSelectFilters).reduce((acc, filter) => {
    if (!acc[filter.parentParam]) {
      acc[filter.parentParam] = []
    }
    acc[filter.parentParam].push(filter)
    return acc
  }, {} as Record<string, TypeFilter[]>)

  // IMPORTANT: Remove parent groups that have no values left
  const nonEmptyGroups = Object.entries(grouped).reduce((acc, [key, filters]) => {
    if (filters.length > 0) {
      acc[key] = filters
    }
    return acc
  }, {} as Record<string, TypeFilter[]>)

  return nonEmptyGroups
}

const getAvailableBrandFilters = (data: TypeCmsVariant[]) => {
  const brandFilters = data.reduce((acc, item) => {
    const brand = item.product?.brand
    if (brand) {
      acc[brand.id] = { id: brand.id, name: brand.name, slug: brand.slug[LANG]}
    }
    return acc
  }, {} as Record<string, TypeFilter>)

  return Object.values(brandFilters)
}


const getFilteredData = (data: TypeCmsVariant[], activeFilters: TypeActiveFilter[]) => {
  if (!activeFilters?.length) return data

  return data.filter(variant => {
    return activeFilters.every(filter => {
      // Handle brands
      if (filter.param === 'brand') {
        const brandSlugs = Array.isArray(filter.value) ? filter.value.map(v => v.slug) : [filter.value.slug]
        return variant.product?.brand && brandSlugs.includes(variant.product.brand.slug[LANG])
      }

      // Skip sort filter (not a product property filter)
      if (filter.param === 'sort') {
        return true
      }

      // Handle frozen filter specially
      if (filter.param === FROZEN_FILTER_TRANSLATION[LANG].slug) {
        return variant.deliveryType === E_DELIVERY_TYPE.FROZEN
      }

      // Handle SELECT filters
      // Check if this is a SELECT filter by looking for it in product filters
      const selectFilter = variant.product?.filters?.find(
        item => item.slug?.[LANG] === filter.param && item.type === E_FILTER_TYPE.SELECT
      )

      if (selectFilter) {
        // Get selected value slugs from activeFilter
        const selectedSlugs = Array.isArray(filter.value)
          ? filter.value.map(v => v.slug)
          : [filter.value.slug]

        // Check if product has ANY of the selected values
        return selectFilter.values?.some(item => selectedSlugs.includes(item.value)) ?? false
      }

      // Handle boolean filters
      // The filter.param is the filter's slug (like 'eco', 'vegan', etc.)
      // Check if the variant's product has a filter with matching slug
      const hasBooleanFilter = variant.product?.filters?.some(
        productFilter => productFilter.slug?.[LANG] === filter.param
      )
      return hasBooleanFilter ?? false
    })
  })
}

const getSortedData = (data: TypeCmsVariant[], activeFilters: TypeActiveFilter[]) => {
  // Find the sort filter
  const sortFilter = activeFilters.find(filter => filter.param === 'sort')
  const sortValue = sortFilter && !Array.isArray(sortFilter.value)
    ? sortFilter.value.slug
    : 'recommended'

  // Return early if recommended (no sorting needed)
  if (sortValue === 'recommended') return data

  // Create a copy to avoid mutating original array
  const sortedData = [...data]

  switch (sortValue) {
    case 'highest_price':
      return sortedData.sort((a, b) =>
        (b.price?.salePrice ?? 0) - (a.price?.salePrice ?? 0)
      )
    case 'lowest_price':
      return sortedData.sort((a, b) =>
        (a.price?.salePrice ?? 0) - (b.price?.salePrice ?? 0)
      )
    default:
      return sortedData
  }
}

const getItems = (data: Props['data']) => {
  return data.map((item, idx) => (
    <Li key={item.id} initial='initial' animate='animate' exit='exit' variants={variantsLi}>
      <VariantCard data={{...item, idx }} onClick={() => {
        // TEMP fix structure for select_item
        // pushAnalyticsEvent?.('select_item', { product: { ...item, idx: idx + 1, gaList, LANG }}, COUNTRY_CODE)
      }}/>
    </Li>
  ))
}

const headingId = 'list_catalog_products'
const sectionName = 'List (catalog products)'
export const SectionProductList: FC<Props> = memo(({ data, gaList = { id: headingId, name: sectionName } }) => {
  console.log('PENDING -> push GA events:', gaList)
  const { t } = useTranslator()
  const pathname = usePathname()
  const { isActiveProduct } = useProduct()
  const { setCurrentFilters, activeFilters } = useFilters()
  const [filteredData, setFilteredData] = useState(() => {
    const filtered = getFilteredData(data, activeFilters)

    return getSortedData(filtered, activeFilters)
  })
  const Items = useMemo(() => getItems(filteredData), [filteredData])

  useEffect(() => {
    if(!data) return
    const booleanFilters = getAvailableBooleanFilters(data)
    const brandFilters = getAvailableBrandFilters(data)
    const selectFilters = getAvailableSelectFilters(data)

    setCurrentFilters({ brands: brandFilters, ...booleanFilters, ...selectFilters })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  useEffect(() => {
    const filtered = getFilteredData(data, activeFilters)
    const sorted = getSortedData(filtered, activeFilters)
    setFilteredData(sorted)
  }, [data, activeFilters])

  return (
    <Section aria-labelledby={headingId} $compactedGrid={isActiveProduct}>
      <h2 id={headingId} className='grid-title'></h2>
      {Items.length > 0 ? (
        <ul>{Items}</ul>
      ) : (
        <NoProductsMessage
          initial={'inactive'}
          animate={'active'}
          exit={'inactive'}
          variants={variantsMessage}
          key={pathname}
        >
          {t('project.shop.no_products')}
        </NoProductsMessage>
      )}
    </Section>
  )
})

const Section = styled.section<{ $compactedGrid?: boolean }>`
  grid-column: 1 / span 6;
  grid-row: auto / span 1;
  height: fit-content;
  margin-bottom: 100px;
  margin-top: 0;
  min-height: 400px;

  ${mq.greaterThan('tablet')} {
    grid-column: 4 / span 9;
    grid-row: 1 / span 2;
    margin-bottom: 200px;
    margin-top: 70px;
    transform: ${({ $compactedGrid }) =>
    $compactedGrid ? 'translateX(-25vw)' : 'translateX(0)'};
    transition: 200ms transform ease-in-out, 200ms width ease-in-out;
    width: ${({ $compactedGrid }) => !$compactedGrid ? '100%' : `calc(100% - ${vw(defaultGrid.tablet.columns.width * 2 + defaultGrid.tablet.gutter.width / 2,'tablet')})`};
  }

	${mq.greaterThan('desktop-wqhd')} {
		transform: translateX(0);
		width: 100%;
	}

  > ul {
    display: grid;
    gap: 10px;
    grid-auto-flow: row dense;
    grid-template-columns: repeat(1, minmax(0, 1fr));
    height: 100%;
    width: 100%;

    ${mq.greaterThan('mobile-xs')} {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    ${mq.greaterThan('nexus7')} {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    ${mq.greaterThan('desktop-xs')} {
      grid-template-columns: repeat(4, minmax(0, 1fr));
    }

    ${mq.greaterThan('desktop')} {
      grid-template-columns: repeat(auto-fill, minmax(190px, 250px));
      justify-content: start; /* left-align the “leftover” space */
    }
  }
`

const Li = styled(motion.li)`
  grid-column: span 1;
  height: fit-content;

  /* Double column layout (gift cards or highlighted products) */
  &.db {
    grid-column: span 2;
  }
`

const NoProductsMessage = styled(motion.p)`
  ${presets.font15}
  font-weight: 400;
  opacity: 0.4;
  padding: ${vw(40, 'mobile')} ${vw(20, 'mobile')};
  text-align: center;

  ${mq.greaterThan('nexus7')} {
    padding: ${vw(40, 'tablet')} ${vw(20, 'tablet')};
  }

  ${mq.greaterThan('tablet')} {
    grid-column: 4 / span 9;
    margin: 0 auto;
    max-width: ${vw(120 * 6, 'desktop')};
    padding: ${vw(40, 'desktop')} 0;
  }

  ${mq.greaterThan('desktop')} {
    max-width: 720px;
    padding: 40px 0;
  }
`

const transition = getTransition()

const variantsLi:Variants = {
  initial: {
    y: 10,
    opacity: 0,
    transition: {
      ...transition,
    },
  },
  animate: {
    y: 0,
    opacity: 1,
    transition: {
      ...transition,
    },
  },
  exit: {
    y: 10,
    opacity: 0,
    transition: {
      ...transition,
    },
  },
}

const variantsMessage:Variants = {
  active: {
    opacity: 1,
    transition: {
      ...transition,
    },
  },
  inactive: {
    opacity: 0,
    transition: {
      ...transition,
    },
  },
}
